import React, { DOMAttributes, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  CalendarDate,
  getLocalTimeZone,
  getWeeksInMonth,
  GregorianCalendar,
  isToday,
  today,
  parseDate,
} from '@internationalized/date'
import { useCalendarGrid, useDateFormatter, useLocale, AriaButtonProps } from 'react-aria'
import { CalendarState, RangeCalendarState } from 'react-stately'
import { SvgChevronLeft, SvgChevronRight } from '@chilipiper/icons/src/design-system'
import { Box, Flex, Text } from '../../../core-components'
import { IconButton } from '../../action'
import { DateItem } from './date-item/DateItem'
import { YearMonthSelect } from './year-month-select/YearMonthSelect'

export type ActiveView = 'all' | 'year' | 'month'

interface Props {
  INTERNAL_defaultActiveView?: ActiveView
  nextButtonProps: AriaButtonProps<'button'>
  onSelectKeyDown?: DOMAttributes<HTMLButtonElement>['onKeyDown']
  prevButtonProps: AriaButtonProps<'button'>
  state: CalendarState | RangeCalendarState
  onVisibleDateChange: (date: CalendarDate) => void
}

export const BaseCalendar = ({
  INTERNAL_defaultActiveView,
  nextButtonProps,
  onSelectKeyDown,
  prevButtonProps,
  state,
  onVisibleDateChange,
}: Props) => {
  const monthFormatter = useDateFormatter({
    month: 'long',
    timeZone: state.timeZone,
  })
  const yearFormatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone,
  })
  const selectedYear = yearFormatter.format(state.focusedDate.toDate(state.timeZone))
  const selectedMonth = monthFormatter.format(state.focusedDate.toDate(state.timeZone))

  const months = useMemo(() => {
    const monthStart = parseDate('2021-01-01')
    return [...new Array(12).keys()].map(index => {
      const date = monthStart.add({ months: index })
      const dateDay = date.toDate(state.timeZone)
      return monthFormatter.format(dateDay)
    })
  }, [monthFormatter, state.timeZone])

  const years = useMemo(() => {
    const now = today(state.timeZone)
    return [...new Array(200).keys()].map(index => {
      const date = now.add({ years: index - 100 })
      const dateDay = date.toDate(state.timeZone)
      return yearFormatter.format(dateDay)
    })
  }, [state.timeZone, yearFormatter])

  const [activeView, setActiveView] = useState<ActiveView>(INTERNAL_defaultActiveView ?? 'all')

  return (
    <Box>
      <StyledFlex bg='bg/section' p={2}>
        <IconButton
          icon={SvgChevronLeft}
          label='Previous month'
          size={7}
          variant='ghost'
          ariaProps={prevButtonProps}
        />
        <Flex flexGrow={1} justifyContent='center'>
          <YearMonthSelect
            value={selectedMonth}
            isOpen={activeView === 'month'}
            onOpenChange={newVal => setActiveView(newVal ? 'month' : 'all')}
            onChange={newMonth => {
              const newDate = state.focusedDate.set({ month: months.indexOf(newMonth) + 1 })
              onVisibleDateChange(newDate)
            }}
            onKeyDown={onSelectKeyDown}
            label='Month'
            options={months}
          />

          <YearMonthSelect
            onKeyDown={onSelectKeyDown}
            isOpen={activeView === 'year'}
            onOpenChange={newVal => setActiveView(newVal ? 'year' : 'all')}
            onChange={newYear => {
              const newDate = state.focusedDate.set({
                year: Number(newYear),
              })
              onVisibleDateChange(newDate)
            }}
            value={selectedYear}
            label='Year'
            options={years}
          />
        </Flex>
        <IconButton
          icon={SvgChevronRight}
          label='Next month'
          size={7}
          variant='ghost'
          ariaProps={nextButtonProps}
        />
      </StyledFlex>
      <CalendarGrid state={state} />
    </Box>
  )
}

const StyledFlex = styled(Flex)`
  box-shadow: ${({ theme }) => theme.shadows['border/row']};
`

const CalendarGrid = ({ state }: { state: RangeCalendarState | CalendarState }) => {
  const { locale } = useLocale()
  const localTimeZone = getLocalTimeZone()
  const { gridProps, headerProps, weekDays } = useCalendarGrid({ weekdayStyle: 'short' }, state)

  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale)

  return (
    <StyledTable cellSpacing={0} as='table' px={4} pb={3} mx='auto' {...gridProps}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map(day => (
            <th key={day}>
              <Flex h={8} alignItems='center' justifyContent='center'>
                <Text textStyle='detail-m' color='fg/moderate'>
                  {day}
                </Text>
              </Flex>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map(weekIndex => (
          <Box as='tr' key={weekIndex}>
            {state.getDatesInWeek(weekIndex).map((date, i) => {
              if (!date) {
                return <td key={i} />
              }
              return (
                <DateItem
                  variant={isToday(date, localTimeZone) ? 'today' : 'default'}
                  key={date.toString()}
                  isEndWeek={i === 6}
                  isStartWeek={i === 0}
                  state={state}
                  date={date}
                />
              )
            })}
          </Box>
        ))}
      </tbody>
    </StyledTable>
  )
}

const StyledTable = styled(Box)`
  border-collapse: separate;
  border-spacing: 0 ${({ theme }) => theme.space[1]};
`

/** Set visible date whenever the date prop changes */
export const useSyncVisibleDate = (
  date: CalendarDate | null,
  setVisibleDate: (date: CalendarDate) => void
) => {
  const [previousForceVisibleDate, setPreviousForceVisibleDate] = useState(date)
  if (previousForceVisibleDate?.toString() !== date?.toString()) {
    setPreviousForceVisibleDate(date)
    if (date) {
      setVisibleDate(date)
    }
  }
}

export const createCalendar = (identifier: string) => {
  if (identifier === 'gregory') {
    return new GregorianCalendar()
  }
  throw new Error(`Unsupported calendar ${identifier}`)
}
